import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Calendar, 
  Wrench, 
  FileText, 
  Receipt, 
  TrendingUp,
  Award,
  ChevronRight,
  Bell,
  LayoutGrid,
  LayoutList,
  Users,
  Car,
  Package,
  Truck,
  ClipboardList,
  Settings,
  BarChart3,
  BookOpen,
  CalendarClock,
  Hammer,
  Newspaper,
  Zap,
  ExternalLink,
  MoreHorizontal,
  Clock,
  AlertCircle,
  ChevronDown,
  Target,
  Tv,
  Recycle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Area, AreaChart } from "recharts";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard({ user }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [pvToken, setPvToken] = useState(null);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);
  const [partsToOrder, setPartsToOrder] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [revenuePeriod, setRevenuePeriod] = useState('month');

  // Redirect RICAMBISTA users to their dedicated dashboard
  useEffect(() => {
    const userType = (user?.user_type || "").toUpperCase();
    if (userType === "RICAMBISTA") {
      navigate("/ricambista", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchDashboardData();
    fetchPVToken();
    fetchRecentQuotes();
    fetchActiveJobs();
    fetchPartsToOrder();
    fetchRevenueData();
  }, []);

  useEffect(() => {
    fetchRevenueData();
  }, [revenuePeriod]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, aptsRes, commsRes] = await Promise.all([
        authFetch(`${API}/stats/dashboard`, { }),
        authFetch(`${API}/appointments?status=scheduled`, { }),
        authFetch(`${API}/communications`, { })
      ]);

      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      if (aptsRes.ok) {
        const aptsData = await aptsRes.json();
        setAppointments(aptsData.slice(0, 5));
      }
      if (commsRes.ok) {
        const commsData = await commsRes.json();
        // Filter unread communications
        setCommunications(commsData.filter(c => !c.read_by?.includes(user?.user_id)).slice(0, 3));
      }
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const fetchPVToken = async () => {
    try {
      const res = await authFetch(`${API}/auth/me`, { });
      if (res.ok) {
        const userData = await res.json();
        if (userData.pv_token) {
          setPvToken(userData.pv_token);
        }
      }
    } catch (error) {
      console.error("Error fetching PV token:", error);
    }
  };

  const fetchRecentQuotes = async () => {
    try {
      const res = await authFetch(`${API}/quotes?limit=4`, { });
      if (res.ok) {
        const data = await res.json();
        setRecentQuotes(data.slice(0, 4));
      }
    } catch (error) {
      console.error("Error fetching quotes:", error);
    }
  };

  const fetchActiveJobs = async () => {
    try {
      const res = await authFetch(`${API}/vehicle-acceptances?status=in_repair,in_diagnosis,received`, { });
      if (res.ok) {
        const data = await res.json();
        setActiveJobs(data.slice(0, 3));
      }
    } catch (error) {
      console.error("Error fetching active jobs:", error);
    }
  };

  const fetchPartsToOrder = async () => {
    try {
      const res = await authFetch(`${API}/modules/articoli/low-stock/list`, { });
      if (res.ok) {
        const data = await res.json();
        setPartsToOrder(data.slice(0, 3));
      }
    } catch (error) {
      console.error("Error fetching parts to order:", error);
    }
  };

  const fetchRevenueData = async () => {
    try {
      const res = await authFetch(`${API}/stats/revenue-chart?period=${revenuePeriod}`, { });
      if (res.ok) {
        const data = await res.json();
        if (data && data.length > 0) {
          setRevenueData(data);
          return;
        }
      }
      setRevenueData([]);
    } catch (error) {
      setRevenueData([]);
    }
  };
  
  // Calcola il totale dal grafico
  const chartTotal = revenueData.reduce((sum, item) => sum + (item.value || 0), 0);

  const handleTileClick = (tile) => {
    if (tile.isPV) {
      if (pvToken) {
        // Open in new tab
        window.open(`/r/${pvToken}`, "_blank");
      } else {
        toast.info("Richiesta Rapida Ricambi non attivato", {
          description: "Contatta l'amministratore per abilitare questa funzione"
        });
      }
    } else {
      navigate(tile.path);
    }
  };

  const markCommunicationRead = async (commId) => {
    try {
      await authFetch(`${API}/communications/${commId}/mark-read`, {
        method: "POST"
      });
      setCommunications(prev => prev.filter(c => c.communication_id !== commId));
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  // Grid tiles for alternative view
  const gridTiles = [
    { 
      label: "Richiesta Rapida Ricambi", 
      icon: Zap,
      color: "from-green-500 to-green-600",
      path: "/pv-link",
      description: "Richiesta rapida ricambi",
      isPV: true
    },
    { 
      label: "Appuntamenti", 
      icon: Calendar,
      color: "from-blue-500 to-blue-600",
      path: "/appointments",
      description: "Gestisci appuntamenti"
    },
    { 
      label: "Accettazioni", 
      icon: ClipboardList,
      color: "from-emerald-500 to-emerald-600",
      path: "/acceptances",
      description: "Veicoli in lavorazione"
    },
    { 
      label: "Preventivi", 
      icon: FileText,
      color: "from-orange-500 to-orange-600",
      path: "/quotes",
      description: "Crea e gestisci preventivi"
    },
    { 
      label: "Fatture", 
      icon: Receipt,
      color: "from-purple-500 to-purple-600",
      path: "/invoices",
      description: "Fatturazione elettronica"
    },
    { 
      label: "Clienti", 
      icon: Users,
      color: "from-cyan-500 to-cyan-600",
      path: "/customers",
      description: "Anagrafica clienti"
    },
    { 
      label: "Veicoli", 
      icon: Car,
      color: "from-pink-500 to-pink-600",
      path: "/vehicles",
      description: "Archivio veicoli"
    },
    { 
      label: "Richieste Ricambi", 
      icon: Package,
      color: "from-amber-500 to-amber-600",
      path: "/parts-requests",
      description: "Ordina ricambi"
    },
    { 
      label: "Fornitori", 
      icon: Truck,
      color: "from-indigo-500 to-indigo-600",
      path: "/suppliers",
      description: "Anagrafica fornitori"
    },
    { 
      label: "Ordini di Lavoro", 
      icon: Hammer,
      color: "from-red-500 to-red-600",
      path: "/work-orders",
      description: "Gestisci lavori"
    },
    { 
      label: "Agenda Lavori", 
      icon: CalendarClock,
      color: "from-teal-500 to-teal-600",
      path: "/work-schedule",
      description: "Pianifica interventi"
    },
    { 
      label: "Magazzino", 
      icon: Package,
      color: "from-lime-500 to-lime-600",
      path: "/magazzino",
      description: "Gestione scorte"
    },
    { 
      label: "Statistiche", 
      icon: BarChart3,
      color: "from-violet-500 to-violet-600",
      path: "/statistiche",
      description: "Analisi e report"
    },
    { 
      label: "DDT", 
      icon: BookOpen,
      color: "from-rose-500 to-rose-600",
      path: "/ddt",
      description: "Documenti di trasporto"
    },
    { 
      label: "Configurazioni", 
      icon: Settings,
      color: "from-slate-500 to-slate-600",
      path: "/settings",
      description: "Impostazioni officina"
    },
    { 
      label: "News", 
      icon: Newspaper,
      color: "from-sky-500 to-sky-600",
      path: "/news",
      description: "Novita e aggiornamenti"
    },
    { 
      label: "Video Riparazioni", 
      icon: Tv,
      color: "from-red-500 to-red-600",
      path: "/video-riparazioni",
      description: "Libreria video tecnica"
    },
    ...(user?.rentri_enabled || user?.role?.toUpperCase() === "ADMIN" ? [{
      label: "RENTRI Rifiuti", 
      icon: Recycle,
      color: "from-emerald-400 to-teal-600",
      path: "/rentri",
      description: "Registro tracciabilita rifiuti"
    }] : []),
  ];

  const kpiCards = [
    { 
      label: "Appuntamenti Oggi", 
      value: stats?.appointments_today || 0, 
      icon: Calendar,
      color: "text-blue-400",
      path: "/appointments"
    },
    { 
      label: "Lavori Attivi", 
      value: stats?.active_jobs || 0, 
      icon: Wrench,
      color: "text-yellow-400",
      path: "/acceptances"
    },
    { 
      label: "Preventivi in Attesa", 
      value: stats?.pending_quotes || 0, 
      icon: FileText,
      color: "text-primary",
      path: "/quotes"
    },
    { 
      label: "Fatture Non Pagate", 
      value: stats?.unpaid_invoices || 0, 
      icon: Receipt,
      color: "text-red-400",
      path: "/invoices"
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="text-zinc-500 mt-1">Benvenuto, {user?.name}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-zinc-800 rounded-lg p-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('classic')}
              className={`px-3 py-1.5 rounded-md transition-all ${
                viewMode === 'classic' 
                  ? 'bg-primary text-black' 
                  : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="view-classic-btn"
            >
              <LayoutList className="w-4 h-4 mr-1.5" />
              Classica
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('grid')}
              className={`px-3 py-1.5 rounded-md transition-all ${
                viewMode === 'grid' 
                  ? 'bg-primary text-black' 
                  : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="view-grid-btn"
            >
              <LayoutGrid className="w-4 h-4 mr-1.5" />
              Griglia
            </Button>
          </div>
          <Button 
            data-testid="new-appointment-btn"
            onClick={() => navigate("/appointments")}
            className="btn-primary"
          >
            Nuovo Appuntamento
          </Button>
        </div>
      </div>

      {/* Communications Banner */}
      {communications.length > 0 && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="w-5 h-5 text-primary" />
            <span className="text-white font-medium">Comunicazioni</span>
          </div>
          <div className="space-y-2">
            {communications.map((comm) => (
              <div 
                key={comm.communication_id}
                className="flex items-center justify-between bg-zinc-900/50 rounded-lg p-3"
              >
                <div>
                  <p className="text-white font-medium">{comm.title}</p>
                  <p className="text-zinc-500 text-sm">{comm.message}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => markCommunicationRead(comm.communication_id)}
                  className="text-zinc-400 hover:text-white"
                >
                  Chiudi
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* GRID VIEW */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {gridTiles.map((tile, index) => (
            <button
              key={index}
              onClick={() => handleTileClick(tile)}
              className={`relative group p-6 rounded-xl bg-gradient-to-br ${tile.color} 
                hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl
                flex flex-col items-center justify-center text-center min-h-[140px]`}
              data-testid={`grid-tile-${tile.label.toLowerCase().replace(/ /g, "-")}`}
            >
              {tile.isPV && (
                <ExternalLink className="absolute top-2 right-2 w-4 h-4 text-white/50" />
              )}
              <tile.icon className="w-10 h-10 text-white mb-3 drop-shadow-md" />
              <span className="text-white font-bold text-sm uppercase tracking-wide">
                {tile.label}
              </span>
              <span className="text-white/70 text-xs mt-1 hidden group-hover:block">
                {tile.description}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* CLASSIC VIEW - Layout esatto come da immagine */}
      {viewMode === 'classic' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* COLONNA SINISTRA - 2/3 */}
          <div className="lg:col-span-2 space-y-5">
            
            {/* Header con Agenda dropdown */}
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate('/appointments')}
                className="flex items-center gap-2 bg-primary text-black px-4 py-2 rounded-lg font-medium hover:bg-primary/90 transition-colors"
              >
                Agenda di oggi
                <ChevronDown className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-2 text-zinc-400 text-sm">
                <span className="font-semibold text-white">{stats?.active_jobs || 0}</span>
                <span>richieste di intervento</span>
              </div>
            </div>

            {/* Attività da gestire */}
            <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Attività da gestire</h3>
                <div className="flex items-center gap-2 text-zinc-500 text-sm">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date().toLocaleDateString('it-IT')}</span>
                </div>
              </div>
              
              <div className="space-y-3">
                {/* Appuntamenti oggi */}
                <div 
                  className="flex items-center justify-between py-2 border-b border-zinc-800 cursor-pointer hover:bg-zinc-800/30 rounded px-2 -mx-2 transition-colors"
                  onClick={() => navigate('/appointments')}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-white">{stats?.appointments_today || 0}</span>
                    <span className="text-zinc-400">appuntamenti oggi</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-500">
                    <span className="text-sm">T+</span>
                    <Calendar className="w-4 h-4" />
                  </div>
                </div>
                
                {/* Accettazioni in corso */}
                <div 
                  className="flex items-center justify-between py-2 border-b border-zinc-800 cursor-pointer hover:bg-zinc-800/30 rounded px-2 -mx-2 transition-colors"
                  onClick={() => navigate('/acceptances')}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-white">{activeJobs.length || 0}</span>
                    <span className="text-zinc-400">accettazioni in corso</span>
                  </div>
                  <span className="text-zinc-500 font-mono text-sm">{(activeJobs.length * 1.0).toFixed(2)}</span>
                </div>
                
                {/* Richieste di intervento */}
                <div 
                  className="flex items-center justify-between py-2 border-b border-zinc-800 cursor-pointer hover:bg-zinc-800/30 rounded px-2 -mx-2 transition-colors"
                  onClick={() => navigate('/work-orders')}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-white">{stats?.active_jobs || 0}</span>
                    <span className="text-zinc-400">richieste di intervento</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-24 h-2 bg-zinc-700 rounded-full overflow-hidden">
                      <div className="h-full bg-zinc-400 rounded-full" style={{ width: stats?.active_jobs ? '60%' : '0%' }}></div>
                    </div>
                    <span className="text-zinc-500 font-mono text-sm">{((stats?.monthly_revenue || 0) / 1000).toFixed(3)}</span>
                  </div>
                </div>
                
                {/* Preventivi da inviare */}
                <div 
                  className="flex items-center justify-between py-2 cursor-pointer hover:bg-zinc-800/30 rounded px-2 -mx-2 transition-colors"
                  onClick={() => navigate('/quotes')}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-white">{stats?.pending_quotes || 0}</span>
                    <span className="text-zinc-400">preventivi da inviare</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-500">
                    <span className="text-sm font-mono">{((stats?.pending_quotes || 0) * 3.7).toFixed(1)}</span>
                    <FileText className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </div>

            {/* Lavorazioni in corso */}
            <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Lavorazioni in corso</h3>
                <button 
                  onClick={() => navigate('/acceptances')}
                  className="text-zinc-500 hover:text-white"
                  title="Vedi tutte le lavorazioni"
                >
                  <MoreHorizontal className="w-5 h-5" />
                </button>
              </div>
              
              {activeJobs.length > 0 ? (
                <div className="space-y-4">
                  {activeJobs.slice(0, 1).map((job, index) => (
                    <div 
                      key={job.acceptance_id || index} 
                      className="space-y-3 cursor-pointer hover:bg-zinc-800/30 rounded-lg p-2 -m-2 transition-colors"
                      onClick={() => navigate('/acceptances')}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-zinc-700 rounded-full flex items-center justify-center">
                            <Users className="w-6 h-6 text-zinc-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{job.customer_name || 'Cliente'}</p>
                            <p className="text-zinc-500 text-sm">{job.vehicle_model || 'Veicolo'}</p>
                            <p className="text-zinc-600 text-xs">{job.vehicle_km || '0'} km  09:00</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-1 text-zinc-500 text-sm">
                            <Target className="w-4 h-4" />
                            <span>Priorità alta</span>
                          </div>
                          <div className="flex items-center gap-1 text-zinc-500 text-sm">
                            <Clock className="w-4 h-4" />
                            <span>09:00</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-zinc-400 text-sm">{job.work_description || 'Ripristino livello olio + filtro olio in corso'}</p>
                          <div className="flex items-center gap-2">
                            <Wrench className="w-4 h-4 text-zinc-500" />
                            <span className="text-white font-medium">50%</span>
                          </div>
                        </div>
                        <div className="w-full h-2 bg-zinc-700 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ 
                            width: '50%',
                            background: 'linear-gradient(90deg, #f97316, #eab308)'
                          }}></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div 
                  className="text-center py-6 text-zinc-500 cursor-pointer hover:text-zinc-300 transition-colors"
                  onClick={() => navigate('/acceptances')}
                >
                  Nessuna lavorazione in corso
                  <p className="text-xs mt-1">Clicca per creare una nuova accettazione</p>
                </div>
              )}
            </div>

            {/* Preventivi recenti */}
            <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Preventivi recenti</h3>
                <button 
                  onClick={() => navigate('/quotes')}
                  className="text-zinc-500 hover:text-white"
                  title="Vedi tutti i preventivi"
                >
                  <MoreHorizontal className="w-5 h-5" />
                </button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-zinc-500 text-sm border-b border-zinc-800">
                      <th className="text-left py-2 font-normal">Cliente</th>
                      <th className="text-left py-2 font-normal">Veicolo</th>
                      <th className="text-left py-2 font-normal">Importo</th>
                      <th className="text-left py-2 font-normal">Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentQuotes.length > 0 ? recentQuotes.map((quote, index) => (
                      <tr key={quote.quote_id || index} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer transition-colors" onClick={() => navigate(`/quotes`)}>
                        <td className="py-3 text-white font-medium">{quote.customer_name || 'N/D'}</td>
                        <td className="py-3 text-zinc-400">{quote.vehicle_model || 'N/D'}</td>
                        <td className="py-3 text-white font-mono">€ {(quote.total || 0).toFixed(2)}</td>
                        <td className="py-3">
                          <span className={`px-3 py-1 rounded-md text-xs font-medium ${
                            quote.status === 'approved' ? 'bg-zinc-700 text-zinc-300' :
                            quote.status === 'sent' ? 'bg-zinc-700 text-zinc-300' :
                            quote.status === 'draft' ? 'bg-primary text-black' :
                            quote.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                            quote.status === 'invoiced' ? 'bg-green-500/20 text-green-400' :
                            'bg-zinc-700 text-zinc-300'
                          }`}>
                            {quote.status === 'approved' ? 'Approvato' :
                             quote.status === 'sent' ? 'Inviato' :
                             quote.status === 'draft' ? 'Bozza' :
                             quote.status === 'rejected' ? 'Rifiutato' : 
                             quote.status === 'invoiced' ? 'Fatturato' :
                             quote.status === 'accepted' ? 'Accettato' :
                             quote.status || 'N/D'}
                          </span>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="4" className="py-6 text-center text-zinc-500">
                          Nessun preventivo recente
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* COLONNA DESTRA - 1/3 */}
          <div className="space-y-5">
            {/* Statistiche fatturato */}
            <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-5">
              <h3 className="text-lg font-bold text-white mb-2">
                Statistiche fatturato
              </h3>
              <div className="text-4xl font-bold text-white font-mono mb-1">
                €{chartTotal.toLocaleString('it-IT')}
              </div>
              <p className="text-primary text-sm mb-4">
                {revenuePeriod === 'week' ? 'Ultimi 7 giorni' : 
                 revenuePeriod === 'month' ? 'Questo mese' : 'Quest\'anno'}
                {chartTotal > 0 && ' • +12% rispetto al periodo precedente'}
              </p>
              
              {/* Chart */}
              <div className="h-32 mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={revenueData}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="label" hide />
                    <YAxis hide />
                    <Tooltip 
                      contentStyle={{ 
                        background: '#18181b', 
                        border: '1px solid #3f3f46',
                        borderRadius: '8px'
                      }}
                      labelStyle={{ color: '#fff' }}
                      formatter={(value) => [`€${value.toLocaleString('it-IT')}`, 'Fatturato']}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#f97316" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorRevenue)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              
              {/* Period selector */}
              <div className="flex items-center gap-2 text-sm">
                <button 
                  onClick={() => setRevenuePeriod('week')}
                  className={`px-3 py-1 rounded transition-colors ${revenuePeriod === 'week' ? 'bg-primary/20 text-primary border border-primary/30' : 'text-zinc-500 hover:text-zinc-300'}`}
                >
                  Ultimi 7 giorni
                </button>
                <button 
                  onClick={() => setRevenuePeriod('month')}
                  className={`px-3 py-1 rounded transition-colors ${revenuePeriod === 'month' ? 'bg-primary/20 text-primary border border-primary/30' : 'text-zinc-500 hover:text-zinc-300'}`}
                >
                  Questo mese
                </button>
                <button 
                  onClick={() => setRevenuePeriod('year')}
                  className={`px-3 py-1 rounded transition-colors ${revenuePeriod === 'year' ? 'bg-primary/20 text-primary border border-primary/30' : 'text-zinc-500 hover:text-zinc-300'}`}
                >
                  Quest'anno
                </button>
              </div>
            </div>

            {/* Avvisi */}
            <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Avvisi ({recentQuotes.length || 0})</h3>
              </div>
              
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-zinc-500 border-b border-zinc-800">
                    <th className="text-left py-2 font-normal">Cliente</th>
                    <th className="text-left py-2 font-normal">Importo</th>
                    <th className="text-left py-2 font-normal">Stato</th>
                    <th className="text-left py-2 font-normal">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {recentQuotes.length > 0 ? recentQuotes.slice(0, 4).map((quote, index) => (
                    <tr 
                      key={index} 
                      className="border-b border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer transition-colors"
                      onClick={() => navigate('/quotes')}
                    >
                      <td className="py-2 text-white">{quote.customer_name || 'Cliente'}</td>
                      <td className="py-2 text-zinc-400">€ {(quote.total || 0).toFixed(2)}</td>
                      <td className="py-2">
                        <span className={`w-2 h-2 rounded-full inline-block ${
                          quote.status === 'approved' || quote.status === 'accepted' ? 'bg-green-500' : 
                          quote.status === 'rejected' ? 'bg-red-500' : 
                          quote.status === 'invoiced' ? 'bg-blue-500' :
                          'bg-zinc-500'
                        }`}></span>
                      </td>
                      <td className="py-2">
                        <button className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
                          quote.status === 'draft' ? 'bg-primary text-black' :
                          quote.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                          quote.status === 'invoiced' ? 'bg-green-500/20 text-green-400' :
                          'bg-zinc-700 text-zinc-300'
                        }`}>
                          {quote.status === 'approved' || quote.status === 'accepted' ? 'Approvato' :
                           quote.status === 'sent' ? 'Inviato' :
                           quote.status === 'draft' ? 'Bozza' :
                           quote.status === 'invoiced' ? 'Fatturato' :
                           quote.status === 'rejected' ? 'Rifiutato' : 'Vedi'}
                          <ChevronRight className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="4" className="py-4 text-center text-zinc-500">
                        Nessun avviso
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Ricambi da ordinare */}
            <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Ricambi da ordinare</h3>
                <button 
                  onClick={() => navigate('/magazzino')}
                  className="text-zinc-500 hover:text-white"
                  title="Vai al magazzino"
                >
                  <MoreHorizontal className="w-5 h-5" />
                </button>
              </div>
              
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-zinc-500 border-b border-zinc-800">
                    <th className="text-left py-2 font-normal">Articolo</th>
                    <th className="text-left py-2 font-normal">Richieste</th>
                    <th className="text-left py-2 font-normal">Disponibili</th>
                    <th className="text-left py-2 font-normal">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {partsToOrder.length > 0 ? partsToOrder.map((part, index) => (
                    <tr key={index} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors">
                      <td className="py-2 text-white truncate max-w-[120px]" title={part.descrizione || part.codice}>
                        {part.descrizione || part.codice || 'Ricambio'}
                      </td>
                      <td className="py-2 text-zinc-400">{part.scorta_minima || 0}</td>
                      <td className="py-2 text-zinc-400">{part.giacenza_calcolata || 0}</td>
                      <td className="py-2">
                        <button 
                          onClick={() => navigate('/parts-requests')}
                          className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
                            (part.giacenza_calcolata || 0) === 0 ? 'bg-primary text-black' : 'bg-zinc-700 text-zinc-300'
                          }`}
                        >
                          Ordina
                          <ChevronRight className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="4" className="py-4 text-center text-zinc-500">
                        <span 
                          className="cursor-pointer hover:text-zinc-300 transition-colors"
                          onClick={() => navigate('/magazzino')}
                        >
                          Nessun ricambio sotto scorta
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
